import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import { useDispatch } from "react-redux";
import {
  updateForm,
  updateDpdpNotice,
  publishDpdpNotice,
  getPublicFormNoticeUrl,
} from "../../../redux/actions/formsAction";
import { useFormDetail } from "./FormDetailContext";
import { createFieldFromPreset } from "./formUtils";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Mirrors lib/consent/constants.js CONSENT_PURPOSES (apis) -- kept in sync
// by hand since this is the only place the web app needs the vocabulary.
const PURPOSE_OPTIONS = [
  { value: "marketing_email", label: "Marketing emails" },
  { value: "transactional", label: "Transactional emails" },
  { value: "whatsapp_alerts", label: "WhatsApp alerts" },
  { value: "whatsapp_marketing", label: "WhatsApp marketing" },
];

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi" },
];

const EMPTY_NOTICE = {
  enabled: false,
  purposes: [],
  serviceDescription: "",
  dataItems: [],
  rightsContact: { name: "", email: "" },
  boardComplaintUrl: "",
  language: "en",
  withdrawUrl: "",
  versionHash: null,
  publishedAt: null,
  history: [],
};

export default function FormDpdp() {
  const { id, isNew, formRecord, publicId, reloadForm } = useFormDetail();
  const dispatch = useDispatch();
  const [notice, setNotice] = useState(EMPTY_NOTICE);
  const [savingField, setSavingField] = useState(false);
  const [savingNotice, setSavingNotice] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [snapshotEntry, setSnapshotEntry] = useState(null);

  useEffect(() => {
    if (!formRecord) return;
    setNotice({
      ...EMPTY_NOTICE,
      ...(formRecord.settings?.dpdpNotice || {}),
      rightsContact: {
        name: formRecord.settings?.dpdpNotice?.rightsContact?.name || "",
        email: formRecord.settings?.dpdpNotice?.rightsContact?.email || "",
      },
    });
  }, [formRecord]);

  if (isNew) {
    return (
      <Alert
        type="info"
        showIcon
        message="Save your form first"
        description="Go to Settings, name your form, and click Save. Then you can configure consent and the DPDP notice here."
      />
    );
  }

  const hasConsentField = (formRecord?.fields || []).some(
    (f) => f.type === "consent_marketing",
  );

  const toggleConsentField = async (checked) => {
    setSavingField(true);
    try {
      const currentFields = formRecord?.fields || [];
      const nextFields = checked
        ? [
            ...currentFields,
            {
              ...createFieldFromPreset(
                "consent_marketing",
                currentFields.map((f) => f.name),
              ),
              order: currentFields.length,
            },
          ]
        : currentFields.filter((f) => f.type !== "consent_marketing");

      await dispatch(updateForm({ fields: nextFields }, id));
      message.success(
        checked
          ? "Marketing consent checkbox added to the form."
          : "Marketing consent checkbox removed.",
      );
      reloadForm();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to update the form field.",
      );
    } finally {
      setSavingField(false);
    }
  };

  const saveDraft = async (silent = false) => {
    setSavingNotice(true);
    try {
      const saved = await dispatch(
        updateDpdpNotice(id, {
          enabled: notice.enabled,
          purposes: notice.purposes,
          serviceDescription: notice.serviceDescription,
          dataItems: notice.dataItems,
          rightsContact: notice.rightsContact,
          boardComplaintUrl: notice.boardComplaintUrl,
          language: notice.language,
        }),
      );
      setNotice((prev) => ({ ...prev, ...saved }));
      if (!silent) message.success("Notice draft saved.");
      return saved;
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to save the notice draft.",
      );
      return null;
    } finally {
      setSavingNotice(false);
    }
  };

  const publish = async () => {
    if (!notice.purposes?.length) {
      message.error("Add at least one purpose before publishing.");
      return;
    }
    setPublishing(true);
    try {
      const saved = await saveDraft(true);
      if (!saved) return;
      const published = await dispatch(publishDpdpNotice(id));
      setNotice((prev) => ({ ...prev, ...published }));
      message.success("Notice published.");
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to publish the notice.",
      );
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={5} style={{ marginTop: 0 }}>
          Consent & DPDP
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Configure the marketing consent checkbox and the DPDP Act notice
          shown to visitors of this form.
        </Paragraph>
      </div>

      <Card size="small" title="Marketing consent checkbox">
        <Space align="center">
          <Switch
            checked={hasConsentField}
            loading={savingField}
            onChange={toggleConsentField}
          />
          <Text>
            {hasConsentField
              ? "A marketing consent checkbox is on this form (unticked by default, never required)."
              : "No marketing consent checkbox on this form yet."}
          </Text>
        </Space>
      </Card>

      <Card
        size="small"
        title="DPDP notice"
        extra={
          publicId ? (
            <Button
              size="small"
              type="link"
              href={getPublicFormNoticeUrl(publicId)}
              target="_blank"
              rel="noreferrer"
            >
              Preview
            </Button>
          ) : null
        }
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Space align="center">
            <Switch
              checked={notice.enabled}
              onChange={(checked) =>
                setNotice((prev) => ({ ...prev, enabled: checked }))
              }
            />
            <Text>Show a DPDP notice link on this form</Text>
          </Space>

          {notice.publishedAt ? (
            <Alert
              type="success"
              showIcon
              message={`Published ${new Date(notice.publishedAt).toLocaleString()}`}
              description={
                <Space direction="vertical" size={0}>
                  <Text type="secondary" copyable={{ text: notice.versionHash }}>
                    Version {notice.versionHash?.slice(0, 12)}…
                  </Text>
                  <Button
                    size="small"
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => setHistoryOpen(true)}
                  >
                    View version history ({notice.history?.length || 0})
                  </Button>
                </Space>
              }
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              message="This notice has never been published."
              description="Save a draft, then publish so it appears on the hosted form and public notice page."
            />
          )}

          <div>
            <Text type="secondary">Purposes</Text>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              value={notice.purposes}
              options={PURPOSE_OPTIONS}
              onChange={(purposes) =>
                setNotice((prev) => ({ ...prev, purposes }))
              }
              placeholder="What is the data used for?"
            />
          </div>

          <div>
            <Text type="secondary">Service description</Text>
            <TextArea
              rows={3}
              value={notice.serviceDescription}
              onChange={(e) =>
                setNotice((prev) => ({
                  ...prev,
                  serviceDescription: e.target.value,
                }))
              }
              placeholder="What service is this form part of?"
            />
          </div>

          <div>
            <Text type="secondary">
              Data items collected (auto-filled from form fields — edit if
              needed)
            </Text>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              value={notice.dataItems}
              onChange={(dataItems) =>
                setNotice((prev) => ({ ...prev, dataItems }))
              }
              placeholder="e.g. Name, Email, Phone"
            />
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary">Rights-contact name (optional)</Text>
              <Input
                value={notice.rightsContact.name}
                onChange={(e) =>
                  setNotice((prev) => ({
                    ...prev,
                    rightsContact: { ...prev.rightsContact, name: e.target.value },
                  }))
                }
                placeholder="Falls back to your org's DPDP settings if blank"
              />
            </Col>
            <Col span={12}>
              <Text type="secondary">Rights-contact email (optional)</Text>
              <Input
                value={notice.rightsContact.email}
                onChange={(e) =>
                  setNotice((prev) => ({
                    ...prev,
                    rightsContact: { ...prev.rightsContact, email: e.target.value },
                  }))
                }
                placeholder="Falls back to your org's DPDP settings if blank"
              />
            </Col>
          </Row>

          <div>
            <Text type="secondary">
              Data Protection Board complaint URL (optional)
            </Text>
            <Input
              value={notice.boardComplaintUrl}
              onChange={(e) =>
                setNotice((prev) => ({
                  ...prev,
                  boardComplaintUrl: e.target.value,
                }))
              }
              placeholder="Falls back to your org's DPDP settings if blank"
            />
          </div>

          <div>
            <Text type="secondary">Language</Text>
            <Select
              style={{ width: "100%", maxWidth: 240 }}
              value={notice.language}
              options={LANGUAGE_OPTIONS}
              onChange={(language) =>
                setNotice((prev) => ({ ...prev, language }))
              }
            />
          </div>

          <Space wrap>
            <Button loading={savingNotice} onClick={() => saveDraft(false)}>
              Save draft
            </Button>
            <Button type="primary" loading={publishing} onClick={publish}>
              Publish
            </Button>
          </Space>
        </Space>
      </Card>

      <Modal
        title="Version history"
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
      >
        <List
          dataSource={[...(notice.history || [])].reverse()}
          renderItem={(entry) => (
            <List.Item
              actions={[
                <Button
                  key="view"
                  type="link"
                  size="small"
                  onClick={() => setSnapshotEntry(entry)}
                >
                  View text
                </Button>,
              ]}
            >
              <Space direction="vertical" size={0}>
                <Text>{new Date(entry.publishedAt).toLocaleString()}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {entry.publishedBy} · {entry.versionHash?.slice(0, 12)}…
                </Text>
              </Space>
            </List.Item>
          )}
        />
      </Modal>

      <Modal
        title="Notice text at publish time"
        open={!!snapshotEntry}
        onCancel={() => setSnapshotEntry(null)}
        footer={null}
        width={600}
      >
        {snapshotEntry && (
          <>
            <Tag style={{ marginBottom: 12 }}>
              {new Date(snapshotEntry.publishedAt).toLocaleString()}
            </Tag>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
              {JSON.stringify(snapshotEntry.snapshot, null, 2)}
            </pre>
          </>
        )}
      </Modal>
    </Space>
  );
}
